<script setup lang="ts">
import * as _ from 'lodash-es';
import { onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import { User } from 'oidc-client-ts';
import { useEntityStore, usePracticeStore, useRootStore, useUserStore } from '@/stores';
import { useAppStore, useAuthStore } from '@repo/vue/stores';
import { container as WidgetContainerModal } from 'jenesius-vue-modal';
import { DxLoader, DxLoaderBar, DxLoaderBlock, DxLoaderSpinner, DxToast } from '@repo/vue/components';
import { useDxProgress } from '@repo/vue/utils';
import TwoFactorRequiredModal from '@/views/main/user-profile/TwoFactorRequiredModal.vue';

// utils
const route = useRoute();
const { isLoadingSpinner } = useDxProgress();

// stores
const appStore = useAppStore();
const rootStore = useRootStore();
const entityStore = useEntityStore();
const userStore = useUserStore();
const router = useRouter();
const authStore = useAuthStore();
const practiceStore = usePracticeStore();

// state
const { isIFrame } = storeToRefs(appStore);
const { currentEntity } = storeToRefs(entityStore);
const { currentUser } = storeToRefs(userStore);
const { userProfileId } = storeToRefs(rootStore);
const { isLoading } = storeToRefs(authStore);
// Read at setup so RouterView stays unmounted until logout redirects.
const twoFactorReset = new URLSearchParams(window.location.search).get('twoFactorReset') === '1';
const { currentPractice } = storeToRefs(practiceStore);

// methods
const init = async () => {
  if (route.query.entityId) {
    rootStore.setEntityId(route.query.entityId.toString());
  }
  if (route.query.practiceId) {
    rootStore.setPracticeId(route.query.practiceId.toString());
  }

  currentPractice.value = await practiceStore.fetchCurrent();
  if (currentPractice.value?.id) {
    rootStore.setPracticeId(currentPractice.value?.id);
  }

  currentEntity.value = await entityStore.fetchCurrent();
  currentUser.value = await userStore.fetchCurrent();
  if (currentEntity.value?.id && _.toLower(route.path) === '/files') {
    const pathRoute = { name: 'files', query: {} as any };

    if (route.query.openPath) pathRoute.query.openPath = route.query.openPath;

    if (route.query.openFile) pathRoute.query.openFile = route.query.openFile;

    await router.push(pathRoute);
  } else if (currentEntity.value?.id && _.toLower(route.path) === '/requests') {
    const pathRoute = { name: 'requests', query: {} as any };
    if (route.query.openFile) pathRoute.query.openFile = route.query.openFile;
    if (route.query.requestId) pathRoute.query.requestId = route.query.requestId;
    await router.push(pathRoute);
  } else if (currentEntity.value?.id) {
    await router.push({ name: 'requests' });
  } else {
    await router.push({ name: 'entities' });
  }
};

const clearRootStoreData = () => {
  const search = window.location.search;
  if ((_.includes(search, 'code=') || _.includes(search, 'error=')) && _.includes(search, 'state=')) {
    rootStore.clearCurrentUser();
    rootStore.clearCurrentEntity();
    console.log('rootStore data reset');
  }
};

const setRootStoreData = (user: User) => {
  rootStore.$patch({
    userProfileId: user.profile?.sub
  });

  console.log('rootStore data saved');
};

authStore.$subscribe(
  (mutation, state) => {
    const user = state.user;
    if (user?.access_token) {
      if (user && user.access_token) {
        clearRootStoreData();
        setRootStoreData(user);
      }
    }
  },
  { immediate: true }
);

// watches
watch(
  userProfileId,
  (newValue, oldValue) => {
    if (newValue && newValue !== oldValue) {
    }
  },
  { immediate: true }
);

// hooks
onMounted(async () => {
  // A completed 2FA replacement revokes this session's grants; force a re-login.
  if (twoFactorReset) {
    await authStore.logout();
    return;
  }

  await init();
});
</script>

<template>
  <template v-if="isLoading || twoFactorReset">
    <DxLoader />
  </template>
  <template v-else>
    <RouterView />
    <DxLoaderBlock :blocked="isLoadingSpinner" />
    <DxLoaderBar v-if="!isIFrame" />
    <DxLoaderSpinner />
    <DxToast />
    <ConfirmDialog>
      <template #message="slotProps">
        <Icon icon="line-md:confirm" :class="slotProps.message.icon" />
        <span class="p-confirm-dialog-message" v-html="slotProps.message.message"></span>
      </template>
    </ConfirmDialog>
    <DynamicDialog />
    <WidgetContainerModal />
    <TwoFactorRequiredModal />
  </template>
</template>
